import type { Labels, SalesforcePaymentsConfig } from '../types/config';
import type { PaymentRequest } from '../types/paymentRequest';
import type {
  ComponentType,
  PaymentMethod,
  PaymentMethodSet,
  PaymentMethodType,
  PaymentMode,
  VendorAccount,
} from '../types/paymentMethodSet';
import { DEFAULT_THEME, DEFAULT_THEME_DESIGN_TOKENS, addClass, deepMerge, isObject } from '../util/utility';
import { getVendorHandler } from '../util/load';
import type { SfPaymentMethodComponent } from '../vendor/sfp-payment-method-component';
import type { SfpVendor } from '../vendor/sfp-vendor';
import type { AbstractTemplate } from './template/abstract-template';
import type { SfpVendorWithTemplates } from './template/sfp-vendor-with-templates';
import type SfpContext from '../sfp-context';
import type { MetadataReader } from '../util/metadata-reader';
import type {
  VendorPaymentMethod,
  VendorPaymentMethodElement,
  VendorPaymentMethodElementMapping,
} from '../types/component';

export class Component {
  TYPE: ComponentType;

  template: AbstractTemplate;
  paymentElement: HTMLElement;
  paymentMode: PaymentMode;
  metadataReader: MetadataReader;
  paymentMethodSet: PaymentMethodSet;
  config: SalesforcePaymentsConfig;
  paymentRequest: PaymentRequest;
  paymentMethods: VendorPaymentMethod[] = new Array<VendorPaymentMethod>();
  vendorPaymentMethodElements: VendorPaymentMethodElement[] = new Array<VendorPaymentMethodElement>();
  elementsToMountTo: HTMLElement[] = new Array<HTMLElement>();
  context: SfpContext;
  components: SfPaymentMethodComponent[] = new Array<SfPaymentMethodComponent>();
  selectedPaymentMethod: SfPaymentMethodComponent;

  eventHandlers: Map<string, Function> = new Map<string, Function>();
  isLoaded = false;

  /**
   * @class Component
   * @hideconstructor
   * @classdesc Base class component.
   * @public
   */
  constructor(
    context: SfpContext,
    paymentMode: PaymentMode,
    metadataReader: MetadataReader,
    paymentMethodSet: PaymentMethodSet,
    config: SalesforcePaymentsConfig,
    paymentRequest: PaymentRequest,
    elementOrSelectorToMountTo: string | HTMLElement
  ) {
    this.context = context;
    this.paymentMode = paymentMode;
    this.metadataReader = metadataReader;
    this.paymentMethodSet = paymentMethodSet;
    this.config = config;
    this.paymentRequest = paymentRequest;

    this.formatPaymentRequest();
    this.setElementsToMountTo(elementOrSelectorToMountTo);
    this.createPaymentElement();
    this.stylePaymentElement();
  }

  /**
   * Destroys all elements and other data for this component.
   * @public
   */
  destroy(): void {
    this.components.forEach((component) => component.destroy());
    this.components = new Array<SfPaymentMethodComponent>();
    this.template?.destroy();
    this.template = null;
    this.unmountPaymentElement();
    this.paymentElement = null;
  }

  private setElementsToMountTo(elementOrSelectorToMountTo: string | HTMLElement): void {
    if (elementOrSelectorToMountTo instanceof HTMLElement) {
      // Add the given element to the list
      this.elementsToMountTo.push(elementOrSelectorToMountTo);
    } else {
      // Add elements with the selector string class name
      const classElements: HTMLCollectionOf<Element> = document.getElementsByClassName(elementOrSelectorToMountTo);
      if (classElements.length) {
        for (const classElement of Array.from(classElements)) {
          this.elementsToMountTo.push(classElement as HTMLElement);
        }
      }

      // Add element with the selector string id
      const idElement: HTMLElement = document.getElementById(elementOrSelectorToMountTo);
      if (idElement) {
        this.elementsToMountTo.push(idElement);
      }
    }
  }

  protected createPaymentElement(): void {
    this.paymentElement = document.createElement('div');
  }

  protected stylePaymentElement(): void {
    this.paymentElement.className = 'sfpp-component';

    if (this.config) {
      // Apply default base theme properties
      this.config.theme = deepMerge({}, DEFAULT_THEME, this.config.theme);

      // Apply default design tokens based on theme properties
      this.config.theme.designTokens = deepMerge(
        {},
        DEFAULT_THEME_DESIGN_TOKENS[this.config.theme.type + '-' + this.config.theme.mode],
        this.config.theme.designTokens
      );

      if (this.config.theme.type) {
        // Add theme type class name to root payment element
        addClass(this.paymentElement, `sfpp-theme-${this.config.theme.type}`);
      }
      if (this.config.theme.mode) {
        // Add theme mode class name to root payment element
        addClass(this.paymentElement, `sfpp-mode-${this.config.theme.mode}`);
      }

      // Add CSS variables for theme design tokens
      for (const key in this.config.theme.designTokens) {
        this.paymentElement.style.setProperty('--sfpp-' + key, this.config.theme.designTokens[key]);
      }

      if (this.config.theme.rules) {
        // Add CSS variables for properties in theme rules
        this.setStyleCustomProperties(this.config.theme.rules, '--sfpp-');
      }
    }
  }

  private setStyleCustomProperties(rules: { [k: string]: any }, prefix: string): void {
    let value;
    for (const key in rules) {
      value = rules[key];
      if (typeof value === 'string') {
        // Set custom property for each string value
        this.paymentElement.style.setProperty(prefix + key, value);
      } else if (isObject(value)) {
        // Add to prefix and recurse for each object value
        const keyAsPropertySegment = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        this.setStyleCustomProperties(value, prefix + keyAsPropertySegment + '-');
      }
    }
  }

  private formatPaymentRequest(): void {
    if (this.paymentRequest?.locale) {
      this.paymentRequest.locale = this.paymentRequest.locale.replace('_', '-');
    }
  }

  mountPaymentElement(): void {
    this.unmountPaymentElement();
    for (const elementToMountTo of this.elementsToMountTo) {
      elementToMountTo.appendChild(this.paymentElement);
    }
    this.isLoaded = true;
  }

  unmountPaymentElement(): void {
    for (const elementToMountTo of this.elementsToMountTo) {
      if (elementToMountTo.contains(this.paymentElement)) {
        elementToMountTo.removeChild(this.paymentElement);
      }
      const paymentElements: Element[] = Array.from(elementToMountTo.getElementsByClassName('sfpp-component'));
      paymentElements.forEach((el: Element) => {
        el.remove();
      });
    }
    this.isLoaded = false;
  }

  async updatePaymentMethodSet(paymentMethodSet: PaymentMethodSet): Promise<void> {
    this.paymentMethodSet = paymentMethodSet;
    this.unmountPaymentElement();
    this.createPaymentElement();
    this.stylePaymentElement();
    await this.initialize();
  }

  async updateConfig(config: SalesforcePaymentsConfig): Promise<void> {
    this.config = config;
    this.unmountPaymentElement();
    this.createPaymentElement();
    this.stylePaymentElement();
    await this.initialize();
  }

  async updateLabels(labels: Labels): Promise<void> {
    this.config.labels = labels;
    this.unmountPaymentElement();
    this.createPaymentElement();
    this.stylePaymentElement();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    // Map relevant payment methods in the payment method set to their vendor accounts
    this.mapPaymentMethods();

    // Obtain a vendor-specific payment element template implementation
    await this.obtainTemplate();

    // Map relevant payment methods/vendor accounts to vendor-specific elements to render them
    this.mapPaymentMethodElements();

    // Get payment method components for those vendor-specific elements
    await this.getComponents();

    // Mount the payment element in the application
    this.mountPaymentElement();

    // Use the vendor-specific template to build the element from the payment method components
    this.template.buildElement(this.components);

    this.isLoaded = true;
  }

  private mapPaymentMethods(): void {
    // Find all the component payment methods
    const componentPaymentMethods = this.paymentMethodSet.paymentMethods.filter((paymentMethod) =>
      this.includePaymentMethod(paymentMethod)
    );

    for (const paymentMethod of componentPaymentMethods) {
      // Find the account in the payment method set for the payment method
      const account = this.paymentMethodSet.paymentMethodSetAccounts.find(
        (acct) => acct.accountId === paymentMethod.accountId
      );
      if (account) {
        // Create vendor payment method including the account
        const vendorPaymentMethod: VendorPaymentMethod = {
          paymentMethodType: paymentMethod.paymentMethodType,
          vendorAccount: account,
        };
        this.paymentMethods.push(vendorPaymentMethod);
      } else {
        // Account was not found in the payment method set
        console.warn(`Account ${paymentMethod.accountId} not found for ${paymentMethod.paymentMethodType}`);
        this.context.report({
          code: 'ACCOUNT_NOT_FOUND',
          type: 'PAYMENT_METHOD_SET',
          message: `Account ${paymentMethod.accountId} not found for ${paymentMethod.paymentMethodType}`,
        });
      }
    }
  }

  /**
   * Returns true if the given payment method should be included in this component.
   * By default this returns true if the payment method includes the payment mode
   * corresponding to this component. Subclasses should override and may call super
   * to replace or add to this logic.
   * @param paymentMethod payment method to potentially include
   * @returns if it should be included
   */
  protected includePaymentMethod(paymentMethod: PaymentMethod): boolean {
    return paymentMethod.paymentModes.includes(this.paymentMode);
  }

  private async obtainTemplate(): Promise<void> {
    // Obtain the unique relevant vendor handlers referenced in the payment method set
    for (const vendorAccount of this.paymentMethodSet.paymentMethodSetAccounts) {
      await this.obtainVendorHandler(vendorAccount);
    }

    // Find the first vendor handler that has component templates
    const vendorHandler: SfpVendorWithTemplates = [...this.context.vendorAccountToHandler.values()].find(
      (vendor) => (vendor as SfpVendorWithTemplates).getTemplate !== undefined
    ) as SfpVendorWithTemplates;

    if (!vendorHandler) {
      throw new Error('No vendor in payment method set has component templates');
    }

    // Get the template for this component
    this.template = vendorHandler.getTemplate(this.config, this.metadataReader, this);
  }

  protected async obtainVendorHandler(vendorAccount: VendorAccount): Promise<SfpVendor> {
    let vendorHandler: SfpVendor;
    if (!this.context.vendorAccountToHandler.has(vendorAccount.accountId)) {
      vendorHandler = await getVendorHandler(vendorAccount, this.context, this.config, this.paymentRequest);
      this.context.vendorAccountToHandler.set(vendorAccount.accountId, vendorHandler);
    } else {
      vendorHandler = this.context.vendorAccountToHandler.get(vendorAccount.accountId);
    }
    return vendorHandler;
  }

  private mapPaymentMethodElements(): void {
    for (const vendorPaymentMethod of this.paymentMethods) {
      // Include the payment method if the payment request currency matches its available currencies
      if (
        this.metadataReader.currencyIsApplicable(vendorPaymentMethod.paymentMethodType, this.paymentRequest?.currency)
      ) {
        // Map payment method element to render for this payment method
        const mapping: VendorPaymentMethodElementMapping = this.template.mapVendorPaymentMethodType(
          vendorPaymentMethod.vendorAccount.vendor,
          vendorPaymentMethod.paymentMethodType,
          false
        );

        if (mapping) {
          // Find existing vendor payment method element for this payment method element
          let vendorPaymentMethodElement: VendorPaymentMethodElement = this.vendorPaymentMethodElements.find(
            (e) =>
              e.paymentMethodElement === mapping.paymentMethodElement &&
              e.vendorAccount.vendor === vendorPaymentMethod.vendorAccount.vendor &&
              e.vendorAccount.accountId === vendorPaymentMethod.vendorAccount.accountId
          );

          if (!vendorPaymentMethodElement || mapping.distinct) {
            // Create vendor payment method element for this payment method element
            vendorPaymentMethodElement = {
              vendorAccount: vendorPaymentMethod.vendorAccount,
              paymentMethodElement: mapping.paymentMethodElement,
              paymentMethods: [],
            };
            this.vendorPaymentMethodElements.push(vendorPaymentMethodElement);
          }

          // Add this payment method to the list for this vendor payment method element
          vendorPaymentMethodElement.paymentMethods.push(vendorPaymentMethod);
        } else {
          // Mapping was not found for the vendor payment method
          console.warn(
            `Element not found for ${vendorPaymentMethod.vendorAccount.vendor} to render ${vendorPaymentMethod.paymentMethodType}`
          );
          this.context.report({
            code: 'VENDOR_PAYMENT_METHOD_NOT_FOUND',
            vendor: vendorPaymentMethod.vendorAccount.vendor,
            type: 'PAYMENT_METHOD_SET',
            message: `Element not found for ${vendorPaymentMethod.vendorAccount.vendor} to render ${vendorPaymentMethod.paymentMethodType}`,
          });
        }
      }
    }
  }

  protected async getComponents(): Promise<void> {
    const componentRenderPromises: Promise<PaymentMethodType[]>[] = [];
    for (const vendorPaymentMethodElement of this.vendorPaymentMethodElements) {
      // Obtain handler for the vendor
      const vendorHandler: SfpVendor = await this.obtainVendorHandler(vendorPaymentMethodElement.vendorAccount);

      try {
        // Get component for the payment method element
        const paymentComponent = await vendorHandler.getPaymentMethodComponent(
          this,
          vendorPaymentMethodElement,
          this.metadataReader,
          this.config,
          this.paymentRequest,
          this.paymentMode
        );
        if (this.shouldAddComponent(paymentComponent)) {
          // Add the component to be rendered
          paymentComponent.vendorAccount = vendorPaymentMethodElement.vendorAccount;
          this.components.push(paymentComponent);

          // Keep track of a promise resolved on render
          componentRenderPromises.push(paymentComponent.renderPromise);
        }
      } catch (err) {
        console.warn(`Component creation for ${vendorPaymentMethodElement.paymentMethodElement} failed: ${err}`);
        this.context.report({
          code: 'COMPONENT_CREATION_FAILED',
          vendor: vendorPaymentMethodElement.vendorAccount.vendor,
          message: `Component creation for ${vendorPaymentMethodElement.paymentMethodElement} failed: ${err}`,
        });
      }
    }

    Promise.allSettled(componentRenderPromises).then((results: PromiseSettledResult<PaymentMethodType[]>[]) => {
      // All render promises for payment method components have settled
      const types: PaymentMethodType[] = [];
      results.forEach((result) => {
        if ('value' in result) {
          result.value?.forEach((type: PaymentMethodType) => {
            if (!types.includes(type)) {
              // Include payment method types from successfully resolved promises, without duplicates
              types.push(type);
            }
          });
        }
      });
      this.onComponentsRendered(types);
    });
  }

  /**
   * Returns true if the given payment method component should be added to the payment element.
   * By default this returns true if the component is truthy and has truthy header and element.
   * Subclasses should override this method and may call super to replace or add to this logic.
   * @param paymentComponent payment method component
   * @returns if it should be added
   */
  protected shouldAddComponent(paymentComponent: SfPaymentMethodComponent): boolean {
    if (!paymentComponent || !paymentComponent.element || !paymentComponent.header) {
      // Component must have header and body elements
      return false;
    }
    return true;
  }

  firePaymentMethodEvent(evtName: string, component: SfPaymentMethodComponent, paymentMode?: PaymentMode): void {
    let eventDetails;
    const spmComponent = component.asSavedPaymentMethodComponent();
    if (spmComponent) {
      if (spmComponent.savedPaymentMethod) {
        eventDetails = {
          selectedPaymentMethodId: spmComponent.savedPaymentMethod.id,
          savedPaymentMethodInfo: {
            id: spmComponent.savedPaymentMethod.id,
            subtype: spmComponent.getSubType(),
            title: spmComponent.getTitle(),
            details: spmComponent.getDetails(),
          },
        };
      } else {
        eventDetails = {
          makePaymentMethodDefault: spmComponent.makePaymentMethodDefault,
          savePaymentMethodForFutureUse: spmComponent.savePaymentMethodFuture,
        };
      }
    }

    const paymentMethodType = component.getPaymentMethodType();
    const event = new CustomEvent(evtName, {
      bubbles: true,
      composed: true,
      detail: {
        ...eventDetails,
        ...this.metadataReader.getAdditionalPaymentMethodEventDetails(paymentMethodType),
        selectedPaymentMethod: paymentMethodType,
        paymentMode: paymentMode,
      },
    });
    this.selectedPaymentMethod = component;
    this.paymentElement.dispatchEvent(event);
  }

  private onComponentsRendered(rendered: PaymentMethodType[]): void {
    /**
     * Payment methods rendered event. Bubbled when a component has finished rendering the payment method types
     * which it is going to present.
     *
     * @event sfp:paymentmethodsrendered
     * @property {string[]} detail.rendered - Array of payment method types
     * @public
     */
    // Fire event containing the rendered payment method types
    this.paymentElement.dispatchEvent(
      new CustomEvent('sfp:paymentmethodsrendered', {
        bubbles: true,
        composed: true,
        detail: {
          rendered,
        },
      })
    );
  }

  /**
   * Hides the given payment method component in this component.
   * @param paymentComponent payment method component
   */
  hide(paymentComponent: SfPaymentMethodComponent): void {
    this.template.hide(paymentComponent);
  }

  /**
   * Shows the given payment method component in this component.
   * @param paymentComponent payment method component
   */
  show(paymentComponent: SfPaymentMethodComponent): void {
    this.template.show(paymentComponent);
  }
}
